import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Selectors,
  ActionCreators,
  AlarmUtils,
  DateTimeUtils
} from 'galarm-shared'
import {
  Box,
  Card,
  CardBody,
  CheckBox,
  Layer,
  Text,
  TextInput,
  Heading,
  TextArea
} from 'grommet'
import { Constants, I18n, colorThemes, GlobalConfig } from 'galarm-config'
import { FirebaseProxy, NavigationUtils } from 'galarm-ps-api'
import { Fab, Action } from 'react-tiny-fab'
import { Add, Clock, Close, FormEdit, FormTrash } from 'grommet-icons'
import TintedImage from './TintedImage'
import { SecondaryText } from 'web-components'
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import SearchBar from './SearchBar'

const { defaultChecklistSelector, upgradePurchasedSelector, allTasksSelector } =
  Selectors

const NewTaskFab = ({ onAddTask }) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const [createNewTask, setCreateNewTask] = useState(false)
  const [newTaskName, setNewTaskName] = useState('')

  const onNewTask = () => {
    setCreateNewTask(true)
  }

  return (
    <>
      <Fab
        mainButtonStyles={{
          backgroundColor: colorThemes.getColorTheme(colorScheme).primary
        }}
        style={{
          bottom: 0,
          left: 0
        }}
        icon={<Add color="white" />}
        event="hover"
        onClick={onNewTask}
        text={I18n.t('newTask')}
      />
      {createNewTask && (
        <Layer
          background="lightTint"
          onEsc={() => setCreateNewTask(false)}
          onClickOutside={() => setCreateNewTask(false)}>
          <Box overflow="auto" width="500px" margin="medium" gap="medium">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {I18n.t('enterTaskName')}
              </Heading>
              <Close
                color="textColor"
                onClick={() => setCreateNewTask(false)}
              />
            </Box>
            <TextArea
              autoFocus
              placeholder={I18n.t('taskName')}
              onChange={event => {
                if (event.target.value.endsWith('\n')) {
                  onAddTask(newTaskName)
                  setNewTaskName('')
                  return
                }

                setNewTaskName(event.target.value)
              }}
              value={newTaskName}
            />
          </Box>
        </Layer>
      )}
    </>
  )
}

const NewTaskAndTaskListFab = ({ onAddTask, onAddTaskList }) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const upgradePurchased = useSelector(upgradePurchasedSelector)

  const [createNewTask, setCreateNewTask] = useState(false)
  const [newTaskName, setNewTaskName] = useState('')
  const [createNewTaskList, setCreateNewTaskList] = useState(false)
  const [newTaskListName, setNewTaskListName] = useState('')

  const onNewTask = () => {
    setCreateNewTask(true)
  }

  const onNewTaskList = () => {
    if (!upgradePurchased) {
      NavigationUtils.showGalarmPremiumScreen('taskListPremiumFeature')
      return
    }

    setCreateNewTaskList(true)
  }

  return (
    <>
      <Fab
        mainButtonStyles={{
          backgroundColor: colorThemes.getColorTheme(colorScheme).primary
        }}
        style={{
          bottom: 0,
          left: 0
        }}
        icon={<Add color="white" />}
        event="hover"
        alwaysShowTitle={true}>
        <Action
          text={I18n.t('newTask')}
          onClick={onNewTask}
          style={{
            backgroundColor: colorThemes.getColorTheme(colorScheme).primary
          }}>
          <TintedImage
            tintColor="white"
            src={require('galarm-res/img/web/ic_task.svg').default}
          />
        </Action>
        <Action
          text={I18n.t('newTaskList')}
          onClick={onNewTaskList}
          style={{
            backgroundColor: colorThemes.getColorTheme(colorScheme).primary
          }}>
          <TintedImage
            tintColor="white"
            src={require('galarm-res/img/web/ic_task_list.svg').default}
          />
        </Action>
      </Fab>
      {createNewTask && (
        <Layer
          background="lightTint"
          onEsc={() => setCreateNewTask(false)}
          onClickOutside={() => setCreateNewTask(false)}>
          <Box overflow="auto" width="500px" margin="medium" gap="medium">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {I18n.t('enterTaskName')}
              </Heading>
              <Close
                color="textColor"
                onClick={() => setCreateNewTask(false)}
              />
            </Box>
            <TextArea
              autoFocus
              placeholder={I18n.t('taskName')}
              onChange={event => {
                if (event.target.value.endsWith('\n')) {
                  onAddTask(newTaskName)
                  setNewTaskName('')
                  return
                }

                setNewTaskName(event.target.value)
              }}
              value={newTaskName}
            />
          </Box>
        </Layer>
      )}
      {createNewTaskList && (
        <Layer
          background="lightTint"
          onEsc={() => setCreateNewTaskList(false)}
          onClickOutside={() => setCreateNewTaskList(false)}>
          <Box overflow="auto" width="500px" margin="medium" gap="medium">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {I18n.t('enterTaskListName')}
              </Heading>
              <Close
                color="textColor"
                onClick={() => setCreateNewTaskList(false)}
              />
            </Box>
            <TextInput
              autoFocus
              placeholder={I18n.t('taskListName')}
              onChange={event => setNewTaskListName(event.target.value)}
              value={newTaskListName}
              onKeyUp={event => {
                if (event.key === 'Enter') {
                  onAddTaskList(newTaskListName)
                  setNewTaskListName('')
                  setCreateNewTaskList(false)
                }
              }}
            />
          </Box>
        </Layer>
      )}
    </>
  )
}

// eslint-disable-next-line no-unused-vars
const TaskSummary = ({ task, onChangeItemName, onRemoveItem, onCheckItem }) => {
  const [itemChecked, setItemChecked] = useState(task.checked)
  const [showDeleteItem, setShowDeleteItem] = useState(false)
  const [showEditItem, setShowEditItem] = useState(false)
  const [editedItemName, setEditedItemName] = useState(task.name)
  const dispatch = useDispatch()
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const taskAlarm = useSelector(state =>
    task.alarmId
      ? state.alarms.alarms.find(a => a.id === task.alarmId) || null
      : null
  )
  const dueDateColor =
    taskAlarm && taskAlarm.date < Date.now()
      ? 'red'
      : colorThemes.getColorTheme(colorScheme).primary

  const changeItemName = () => {
    onChangeItemName(task.id, editedItemName)
  }

  const onCheckItemInternal = event => {
    setItemChecked(event.target.checked)
    onCheckItem(task)
  }

  const onRemoveItemInternal = () => {
    NavigationUtils.showAlert(
      I18n.t('confirm'),
      I18n.t('confirmDeleteTask', { taskName: task.name }),
      [
        {
          text: I18n.t('no')
        },
        {
          text: I18n.t('yes'),
          onPress: () => onRemoveItem(task.id)
        }
      ],
      false
    )
  }

  return (
    <Box
      direction="row"
      align="center"
      onMouseEnter={() => setShowDeleteItem(true)}
      onMouseLeave={() => setShowDeleteItem(false)}>
      <CheckBox
        size="large"
        checked={itemChecked}
        onChange={onCheckItemInternal}
      />
      <Box
        flex
        margin={{ vertical: 'xsmall', left: 'small' }}
        onClick={() => setShowEditItem(true)}>
        <Text>
          {task.name}
          {task.checklistName && (
            <Text color="darkTint"> ({task.checklistName})</Text>
          )}
        </Text>
        {taskAlarm && (
          <Box
            direction="row"
            align="center"
            gap="xsmall"
            margin={{ top: 'xsmall' }}
            style={{ cursor: 'pointer' }}
            onClick={event => {
              event.stopPropagation()
              dispatch(ActionCreators.showAlarmDetailsScreen(taskAlarm.id))
            }}>
            <Clock size="small" color={dueDateColor} />
            <Text size="small" color={dueDateColor}>
              {DateTimeUtils.getDateTimeAsString(taskAlarm.date)}
            </Text>
          </Box>
        )}
      </Box>
      {showDeleteItem ? (
        <Box
          onClick={onRemoveItemInternal}
          pad={{ left: 'none', right: 'small' }}>
          <FormTrash />
        </Box>
      ) : (
        <Box pad="small" />
      )}
      {showEditItem && (
        <Layer
          background="lightTint"
          onEsc={() => setShowEditItem(false)}
          onClickOutside={() => setShowEditItem(false)}>
          <Box overflow="auto" width="500px" margin="medium" gap="medium">
            <Box direction="row" align="center" justify="between">
              <Heading level={3} margin="none">
                {I18n.t('editTask')}
              </Heading>
              <Close color="textColor" onClick={() => setShowEditItem(false)} />
            </Box>
            <TextArea
              autoFocus
              placeholder={I18n.t('taskName')}
              onChange={event => {
                if (event.target.value.endsWith('\n')) {
                  changeItemName()
                  setShowEditItem(false)
                  return
                }

                setEditedItemName(event.target.value)
              }}
              value={editedItemName}
            />
          </Box>
        </Layer>
      )}
    </Box>
  )
}

const TaskListSummary = ({ taskList, onEditTaskList, onChooseTaskList }) => {
  const { name, items = [], linkedAlarmIds = [] } = taskList

  const [taskListName, setTaskListName] = useState(name)

  // Show the edit task list dialog
  const [editTaskList, setEditTaskList] = useState(false)

  // Show the edit icon when the mouse is over the task list
  const [showEditTaskList, setShowEditTaskList] = useState(false)

  const dispatch = useDispatch()
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)
  const listAlarmId = linkedAlarmIds[0] || null
  const listAlarm = useSelector(state =>
    listAlarmId
      ? state.alarms.alarms.find(a => a.id === listAlarmId) || null
      : null
  )
  const listAlarmColor =
    listAlarm && listAlarm.date < Date.now()
      ? 'red'
      : colorThemes.getColorTheme(colorScheme).primary

  const numTasks = items.length
  const numCompletedTasks = items.filter(item => item.checked).length

  const onEditTaskListInternal = event => {
    event.stopPropagation()
    setEditTaskList(true)
    return true
  }

  return (
    <Box flex>
      <Box
        flex
        direction="row"
        align="start"
        onMouseEnter={() => setShowEditTaskList(true)}
        onMouseLeave={() => setShowEditTaskList(false)}>
        <Box flex gap="xsmall" onClick={() => onChooseTaskList(taskList.id)}>
          <Text>{name}</Text>
          <SecondaryText>
            {numCompletedTasks > 0
              ? I18n.t('numTasksWithCompleted', {
                  count: numTasks,
                  completed: numCompletedTasks
                })
              : I18n.t('numTasks', { count: numTasks })}
          </SecondaryText>
          {listAlarm && (
            <Box
              direction="row"
              align="center"
              gap="xsmall"
              margin={{ top: 'xsmall' }}
              style={{ cursor: 'pointer' }}
              onClick={event => {
                event.stopPropagation()
                dispatch(ActionCreators.showAlarmDetailsScreen(listAlarm.id))
              }}>
              <Clock size="small" color={listAlarmColor} />
              <Text size="small" color={listAlarmColor}>
                {DateTimeUtils.getDateTimeAsString(listAlarm.date)}
              </Text>
            </Box>
          )}
        </Box>
        {showEditTaskList && (
          <Box onClick={onEditTaskListInternal} pad="small">
            <FormEdit />
          </Box>
        )}
      </Box>
      {editTaskList && (
        <Layer
          background="lightTint"
          onEsc={() => setEditTaskList(false)}
          onClickOutside={() => setEditTaskList(false)}>
          <Box overflow="auto" width="500px" margin="medium" gap="small">
            <Heading level={3} margin="none">
              {I18n.t('editTaskListName')}
            </Heading>
            <TextInput
              autoFocus
              placeholder={I18n.t('taskListName')}
              onChange={event => setTaskListName(event.target.value)}
              value={taskListName}
              onKeyUp={event => {
                if (event.key === 'Enter') {
                  onEditTaskList(taskList, taskListName)
                  setEditTaskList(false)
                }
              }}
            />
          </Box>
        </Layer>
      )}
    </Box>
  )
}

const TaskList = ({ showSearchBar, hideSearchBar }) => {
  const dispatch = useDispatch()

  const allTaskLists = useSelector(state => state.alarms.checklists)
  const defaultTaskList = useSelector(defaultChecklistSelector)
  const allTasks = useSelector(allTasksSelector)

  // Search state
  const [searchText, setSearchText] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  let currentlySelectedTaskList
  let currentlySelectedTaskListId = useSelector(
    state => state.appState.currentlySelectedTaskListId
  )
  if (!currentlySelectedTaskListId) {
    currentlySelectedTaskListId = defaultTaskList?.id
    currentlySelectedTaskList = defaultTaskList
  } else {
    currentlySelectedTaskList = allTaskLists.find(
      taskList => taskList.id === currentlySelectedTaskListId
    )
  }

  useEffect(() => {
    // If default checklist doesn't exist, create it
    if (!defaultTaskList) {
      AlarmUtils.createDefaultTaskList()
    }
  }, [defaultTaskList])

  // Handle search mode
  useEffect(() => {
    if (showSearchBar) {
      setIsSearching(true)
    } else {
      setIsSearching(false)
      setSearchText('')
    }
  }, [showSearchBar])

  // Filter tasks based on search text
  const searchResults = useMemo(() => {
    if (!searchText.trim()) {
      return []
    }

    const searchLower = searchText.toLowerCase()

    return allTasks.filter(task =>
      task.name?.toLowerCase().includes(searchLower)
    )
  }, [allTasks, searchText])

  console.log('currentlySelectedTaskList', currentlySelectedTaskListId)

  const {
    id,
    creator = GlobalConfig.uid,
    name = '',
    items: origItems = [],
    isDefault = false,
    showCompletedItems = true,
    sortOrder = GlobalConfig.defaultChecklistSortOrder,

    // These are not stored on the database
    checklists = []
  } = currentlySelectedTaskList || {}

  let orderedItems = useMemo(() => {
    if (sortOrder === Constants.CHECKLIST_SORT_ORDERS.ALPHABETICAL_ASC) {
      return origItems.slice().sort((a, b) => a.name.localeCompare(b.name))
    } else if (
      sortOrder === Constants.CHECKLIST_SORT_ORDERS.ALPHABETICAL_DESC
    ) {
      return origItems.slice().sort((a, b) => b.name.localeCompare(a.name))
    } else if (sortOrder === Constants.CHECKLIST_SORT_ORDERS.NEWEST_FIRST) {
      return origItems.slice().sort((a, b) => b.order - a.order)
    } else if (sortOrder === Constants.CHECKLIST_SORT_ORDERS.OLDEST_FIRST) {
      return origItems.slice().sort((a, b) => a.order - b.order)
    } else {
      // Default to order by newest first
      return origItems.slice().sort((a, b) => b.order - a.order)
    }
  }, [currentlySelectedTaskList])

  const onChangeItemName = (itemId, newName) => {
    const newItems = orderedItems.map(item => {
      if (item.id === itemId) {
        return { ...item, name: newName }
      }
      return item
    })
    dispatch(
      ActionCreators.updateChecklist({
        id,
        creator,
        name,
        isDefault,
        showCompletedItems,
        items: newItems,
        sortOrder,
        lastUpdatedAt: Date.now()
      })
    )
    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.RENAME_ITEM_IN_CHECKLIST,
      {}
    )
  }

  const onRemoveItem = itemId => {
    const removedItem = orderedItems.find(item => item.id === itemId)
    const newItems = orderedItems
      .filter(item => item.id !== itemId)
      .map((item, index) => ({
        ...item,
        order: index
      }))
    dispatch(
      ActionCreators.updateChecklist({
        id,
        creator,
        name,
        isDefault,
        showCompletedItems,
        items: newItems,
        sortOrder,
        lastUpdatedAt: Date.now()
      })
    )
    if (removedItem?.alarmId) {
      dispatch(ActionCreators.deleteAlarm(removedItem.alarmId))
    }
    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.REMOVE_ITEM_IN_CHECKLIST,
      {}
    )
  }

  const onCheckItem = checkedItem => {
    const newItems = orderedItems.map(item =>
      item.id === checkedItem.id
        ? { ...item, checked: !checkedItem.checked }
        : item
    )
    dispatch(
      ActionCreators.updateChecklist({
        id,
        creator,
        name,
        isDefault,
        showCompletedItems,
        items: newItems,
        sortOrder,
        lastUpdatedAt: Date.now()
      })
    )
    // Delete the alarm when a task is checked (completed), not when unchecked
    if (!checkedItem.checked && checkedItem.alarmId) {
      dispatch(ActionCreators.deleteAlarm(checkedItem.alarmId))
    }
    FirebaseProxy.logEvent(
      checkedItem.checked
        ? Constants.UserAnalyticsEvents.UNCHECK_ITEM_IN_CHECKLIST
        : Constants.UserAnalyticsEvents.CHECK_ITEM_IN_CHECKLIST,
      {}
    )
  }

  const onChooseTaskList = taskListId => {
    dispatch(ActionCreators.setCurrentlySelectedTaskListId(taskListId))
  }

  const onAddTask = taskName => {
    const newItems = [
      ...orderedItems,
      {
        id: GlobalConfig.firebaseHelpers.generatePushKey(
          GlobalConfig.rootFirebaseRef
        ),
        order: orderedItems.length,
        name: taskName,
        checked: false
      }
    ]
    dispatch(
      ActionCreators.updateChecklist({
        id,
        creator,
        name,
        isDefault,
        showCompletedItems,
        items: newItems,
        sortOrder,
        lastUpdatedAt: Date.now()
      })
    )
    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.ADD_ITEM_TO_CHECKLIST,
      {}
    )
  }

  const onAddTaskList = taskListName => {
    const newChecklist = {
      id: GlobalConfig.firebaseHelpers.generatePushKey(
        GlobalConfig.rootFirebaseRef
      ),
      creator: GlobalConfig.uid,
      name: taskListName,
      items: [],
      isDefault: false,
      sortOrder: GlobalConfig.defaultChecklistSortOrder,
      lastUpdatedAt: Date.now(),
      showCompletedItems: true
    }
    dispatch(ActionCreators.addChecklist(newChecklist))
    dispatch(ActionCreators.setCurrentlySelectedTaskListId(newChecklist.id))
    FirebaseProxy.logEvent(Constants.UserAnalyticsEvents.CREATE_CHECKLIST, {})
  }

  const onEditTaskList = (taskList, taskListName) => {
    dispatch(
      ActionCreators.updateChecklist({
        ...taskList,
        name: taskListName,
        lastUpdatedAt: Date.now()
      })
    )
    FirebaseProxy.logEvent(Constants.UserAnalyticsEvents.RENAME_CHECKLIST, {})
  }

  // eslint-disable-next-line no-unused-vars
  const getItemStyle = useMemo(
    () => (isDragging, draggableStyle) => ({
      // some basic styles to make the items look a bit nicer
      userSelect: 'none',
      margin: `0 0 5px 0`,

      // change background colour if dragging
      background: 'transparent',

      // styles we need to apply on draggables
      ...draggableStyle
    }),
    []
  )

  // All hooks must run before this point. Render the loading state only after
  // every hook has been called so the hook order stays stable across renders.
  // Also guard against the case where an id is set but the matching task list
  // can't be found yet, so we never render with an undefined task list.
  if (!currentlySelectedTaskListId || !currentlySelectedTaskList) {
    return (
      <Box flex align="center" justify="center">
        <Text color="darkTint">{I18n.t('loadingTasks')}</Text>
      </Box>
    )
  }

  const reorder = (startIndex, endIndex) => {
    console.log('reordering', startIndex, endIndex)
    if (startIndex === endIndex) {
      return orderedItems
    }

    // Create a new array without mutating the original
    const result = [...orderedItems]
    const itemToMove = result[startIndex]

    // Remove the item from startIndex and insert at endIndex immutably
    const withoutMovedItem = result.filter((_, index) => index !== startIndex)
    const reorderedItems = [
      ...withoutMovedItem.slice(
        0,
        endIndex > startIndex ? endIndex - 1 : endIndex
      ),
      itemToMove,
      ...withoutMovedItem.slice(endIndex > startIndex ? endIndex - 1 : endIndex)
    ]

    // Reassign order values in descending order immutably
    const finalItems = reorderedItems.map((item, index) => ({
      ...item,
      order: reorderedItems.length - index - 1
    }))

    console.log('reordered', finalItems)
    return finalItems
  }

  // eslint-disable-next-line no-unused-vars
  const onDragEnd = result => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const newItems = reorder(result.source.index, result.destination.index)
    dispatch(
      ActionCreators.updateChecklist({
        id,
        creator,
        name,
        isDefault,
        showCompletedItems,
        items: newItems,
        sortOrder,
        lastUpdatedAt: Date.now()
      })
    )

    FirebaseProxy.logEvent(
      Constants.UserAnalyticsEvents.CHANGE_ITEM_ORDER_IN_CHECKLIST,
      {}
    )
  }

  let itemsToShow = orderedItems
  if (!showCompletedItems) {
    itemsToShow = orderedItems.filter(item => !item.checked)
  }

  // If in search mode, render search interface
  if (isSearching && showSearchBar) {
    return (
      <Box flex overflow="auto" style={{ position: 'relative' }}>
        <Box
          flex={{ shrink: 0 }}
          margin={{ horizontal: 'small', top: 'medium' }}>
          <SearchBar
            onChangeText={setSearchText}
            searchText={searchText}
            onCloseSearchBar={hideSearchBar}
            focus
          />
        </Box>
        {searchResults.length > 0 ? (
          <Box flex={{ shrink: 0 }} gap="xsmall" pad="small">
            {searchResults.map(task => (
              <Card
                flex={{ shrink: 0 }}
                key={task.id}
                background="textBackgroundColor">
                <CardBody pad="small" gap="small">
                  <TaskSummary
                    key={task.id + task.name + task.checked}
                    task={task}
                    onChangeItemName={(itemId, newName) => {
                      dispatch(
                        ActionCreators.updateTaskNameInChecklist(
                          task.checklistId,
                          itemId,
                          newName
                        )
                      )
                    }}
                    onRemoveItem={itemId => {
                      dispatch(
                        ActionCreators.removeTaskFromChecklist(
                          task.checklistId,
                          itemId
                        )
                      )
                      if (task.alarmId) {
                        dispatch(ActionCreators.deleteAlarm(task.alarmId))
                      }
                    }}
                    onCheckItem={() => {
                      dispatch(
                        ActionCreators.toggleTaskInChecklist(
                          task.checklistId,
                          task.id
                        )
                      )
                      // Delete the alarm when a task is checked (completed), not when unchecked
                      if (!task.checked && task.alarmId) {
                        dispatch(ActionCreators.deleteAlarm(task.alarmId))
                      }
                    }}
                  />
                </CardBody>
              </Card>
            ))}
          </Box>
        ) : (
          <Box flex align="center" justify="center">
            <Text color="darkTint">
              {searchText.trim()
                ? I18n.t('noTasksFoundForSearch', { searchText })
                : I18n.t('startTypingToSearchTasks')}
            </Text>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      flex
      overflow="auto"
      style={{ position: 'relative', paddingBottom: '75px' }}>
      <Box flex={{ shrink: 0 }} gap="xsmall" pad="small">
        {/* <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="droppable"
            isDropDisabled={false}
            isCombineEnabled={false}
            ignoreContainerClipping={true}>
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}> */}
        {itemsToShow.map(item => {
          return (
            // <Draggable key={item.id} draggableId={item.id} index={index}>
            //   {(provided, snapshot) => (
            //     <div
            //       ref={provided.innerRef}
            //       {...provided.draggableProps}
            //       {...provided.dragHandleProps}
            //       style={getItemStyle(
            //         snapshot.isDragging,
            //         provided.draggableProps.style
            //       )}>
            <Card
              flex={{ shrink: 0 }}
              key={item.id + item.name + item.checked}
              background="textBackgroundColor">
              <CardBody pad="small" gap="small">
                <TaskSummary
                  task={item}
                  onChangeItemName={onChangeItemName}
                  onRemoveItem={onRemoveItem}
                  onCheckItem={onCheckItem}
                />
              </CardBody>
            </Card>
            //     </div>
            //   )}
            // </Draggable>
          )
        })}
        {/* {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext> */}
      </Box>
      {checklists.length > 0 && (
        <Box flex={{ shrink: 0 }}>
          <Box background="lightTint" pad="small">
            <Text color="darkTint">{I18n.t('otherChecklists')}</Text>
          </Box>
          <Box gap="xsmall" pad="small">
            {checklists.map(checklist => {
              return (
                <Card
                  flex={{ shrink: 0 }}
                  key={checklist.id + checklist.name}
                  background="textBackgroundColor">
                  <CardBody direction="row" pad="small" gap="small">
                    <TaskListSummary
                      taskList={checklist}
                      onEditTaskList={onEditTaskList}
                      onChooseTaskList={onChooseTaskList}
                    />
                  </CardBody>
                </Card>
              )
            })}
          </Box>
        </Box>
      )}
      {currentlySelectedTaskListId === defaultTaskList?.id ? (
        <NewTaskAndTaskListFab
          onAddTask={onAddTask}
          onAddTaskList={onAddTaskList}
        />
      ) : (
        <NewTaskFab onAddTask={onAddTask} />
      )}
    </Box>
  )
}

export default TaskList
